<template>
	<div>
		<form @submit.prevent="submit">
			<div class="gh-layout">
				<aside class="gh-intro">
					<h2 class="gh-intro__title">Pricing Engine</h2>
					<p class="gh-intro__desc">
						Email notices of the <strong>Pricing Engine (Beta)</strong>: the finance people get a mail when a quote is sent to
						them, the salesperson gets one when finance decides, blocks or returns the quote. Who counts as finance is set in
						<router-link to="/pricing/admin/people">Pricing Engine (Beta) → People</router-link>.
					</p>
				</aside>

				<section class="gh-accounts">
					<div v-if="loading" class="ada-card text-muted"><i class="fas fa-spinner fa-spin me-1"></i> Loading…</div>

					<template v-else>
						<div v-if="notice" class="alert mb-3" :class="notice.ok ? 'alert-success' : 'alert-danger'" role="alert">
							<button type="button" class="btn-close float-end" aria-label="Close" @click="notice = null"></button>
							{{ notice.text }}
						</div>

						<div class="ada-card mb-3">
							<div class="gh-section__title"><i class="fas fa-envelope me-1"></i> Email notices</div>
							<div class="form-check form-switch">
								<input id="peNotifyMail" class="form-check-input" type="checkbox" role="switch" v-model="form.notify_mail" />
								<label class="form-check-label" for="peNotifyMail">Send the notices by email</label>
							</div>
							<div class="gh-help mt-2">
								Off: nothing is sent, each notice is written to the API log instead.
								<template v-if="mailFrom"> Mail goes out from {{ mailFrom }}.</template>
							</div>
						</div>

						<div class="ada-card mb-3">
							<div class="gh-section__title"><i class="fas fa-vial me-1"></i> Test address</div>
							<div class="gh-field mb-0">
								<label for="peSandbox">Send every notice to this address instead of the real recipients</label>
								<input
									id="peSandbox"
									type="email"
									class="form-control"
									v-model.trim="form.mail_sandbox_to"
									autocomplete="off"
									placeholder="name@townweb.com"
								/>
								<div class="gh-help">
									While set, no finance person or salesperson gets mail; each mail names who would have. Clear it for the real rollout.
								</div>
							</div>
						</div>

						<div class="ada-card mb-3">
							<div class="gh-section__title"><i class="fas fa-users me-1"></i> Finance people who get the notices</div>
							<div v-if="financeRecipients.length" class="gh-help">{{ financeRecipients.join(', ') }}</div>
							<div v-else class="gh-help">
								Nobody. Either no finance person is assigned yet, or every one of them has the email switched off, both in
								<router-link to="/pricing/admin/people">Pricing Engine (Beta) → People</router-link>. Until then a notice to finance is
								only logged.
							</div>
						</div>
					</template>
				</section>
			</div>

			<hr class="gh-divider" />

			<div class="gh-save-row">
				<button class="gh-save-btn" type="submit" :disabled="saving || loading">
					<i class="fas" :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
					Save changes
				</button>
			</div>
		</form>

		<ConfirmModal
			ref="liveModal"
			title="Send real mail?"
			confirm-text="Yes, send real mail"
			confirm-icon="fa-paper-plane"
			icon-class="fa-envelope"
			danger
			:busy="saving"
			@confirm="save"
		>
			Notices are on and no test address is set. From now on the finance people and the salespeople get the mails themselves.
		</ConfirmModal>
	</div>
</template>

<script>
import restApi from '@/api/index'
import ConfirmModal from '@/components/common/ConfirmModal.vue'

const EMPTY = { notify_mail: false, mail_sandbox_to: '' }

export default {
	name: 'PricingSettings',
	components: { ConfirmModal },
	data() {
		return {
			loading: true,
			saving: false,
			form: { ...EMPTY },
			saved: { ...EMPTY },
			financeRecipients: [],
			mailFrom: '',
			notice: null,
		}
	},
	created() {
		this.load()
	},
	methods: {
		apply(data) {
			this.form = { notify_mail: !!data.notify_mail, mail_sandbox_to: data.mail_sandbox_to || '' }
			this.saved = { ...this.form }
			this.financeRecipients = data.finance_recipients || []
			this.mailFrom = data.mail_from || ''
		},
		async load() {
			this.loading = true
			try {
				const { data } = await restApi.get('/pricing-settings')
				this.apply(data)
			} catch (error) {
				this.fail(error)
			} finally {
				this.loading = false
			}
		},
		submit() {
			// Real mail from now on: notices on with no test address, where that was not the case before. Ask first.
			const goesLive = this.form.notify_mail && !this.form.mail_sandbox_to && (!this.saved.notify_mail || !!this.saved.mail_sandbox_to)
			if (goesLive) this.$refs.liveModal.open()
			else this.save()
		},
		async save() {
			this.saving = true
			try {
				const { data } = await restApi.put('/pricing-settings', this.form)
				this.apply(data)
				this.$refs.liveModal.close()
				this.notice = { ok: true, text: this.summary() }
			} catch (error) {
				this.fail(error)
			} finally {
				this.saving = false
			}
		},
		summary() {
			if (!this.saved.notify_mail) return 'Saved. Notices are off: each one is written to the log only.'
			if (this.saved.mail_sandbox_to) return `Saved. Every notice goes to ${this.saved.mail_sandbox_to}.`
			return 'Saved. Notices go to the real recipients.'
		},
		fail(error) {
			const data = (error.response && error.response.data) || {}
			const first = data.error && typeof data.error === 'object' ? Object.values(data.error)[0] : null
			this.notice = { ok: false, text: data.message || (first && first[0]) || 'Something went wrong.' }
		},
	},
}
</script>
